// TLS posture checklist — row list aligned with Layerd-UI ssl-tls/index.html .check-list.

import React from "react";
import {
  Box, Stack, Typography, CircularProgress, Tooltip,
} from "@mui/material";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import MediationOutlinedIcon from "@mui/icons-material/MediationOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import GppBadOutlinedIcon from "@mui/icons-material/GppBadOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckIcon from "@mui/icons-material/Check";

import colors from "../../../../../utils/colors";
import { certsCardTitleSx, certsPillTextSx } from "../certsTypography";
import {
  certsCardShellSx,
  certsPanelHeaderSx,
  certsPostureCheckListSx,
  certsPostureCheckRowSx,
} from "../certsLayout";

const GREEN_DARK = "#0f7a42";
const WARN_INK = "#7a5b10";
const POSTURE_ROW_ICON_SX = {
  fontSize: 15,
  width: 15,
  height: 15,
  color: colors.neutral[500],
  flexShrink: 0,
};

const STATUS_ICON_SX = {
  fontSize: 11,
  width: 11,
  height: 11,
  display: "block",
  color: "inherit",
  "& path": { fill: "currentColor" },
};

function PostureStatusIcon({ warn }) {
  const ok = !warn;
  return (
    <Box
      className="ssl-posture-status-badge"
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: ok ? "#E8FBEF" : "#FFF6D0",
        color: ok ? GREEN_DARK : WARN_INK,
      }}
    >
      {ok
        ? <CheckIcon sx={STATUS_ICON_SX} />
        : <WarningAmberOutlinedIcon sx={STATUS_ICON_SX} />}
    </Box>
  );
}

function PosturePill({ tone, children }) {
  const ok = tone === "ok";
  return (
    <Box sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.625,
      px: 1.125,
      height: 22,
      borderRadius: 999,
      ...certsPillTextSx,
      fontSize: 11.5,
      whiteSpace: "nowrap",
      backgroundColor: ok ? "#E8FBEF" : "#FFF6D0",
      color: ok ? GREEN_DARK : WARN_INK,
      border: `1px solid ${ok ? "#C0EED4" : "#FFE89A"}`,
    }}>
      <Box sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: "currentColor",
        flexShrink: 0,
      }} />
      {children}
    </Box>
  );
}

function PostureCheckRow({
  icon: Icon, label, value, warn, na, tooltip, isLast,
}) {
  return (
    <Box sx={certsPostureCheckRowSx(warn, isLast)}>
      <Stack direction="row" alignItems="center" spacing={1.125} sx={{ minWidth: 0, flex: 1 }}>
        <Icon sx={POSTURE_ROW_ICON_SX} />
        <Typography sx={{
          fontSize: 13,
          fontWeight: 500,
          lineHeight: "20px",
          color: colors.neutral[1000],
          minWidth: 0,
        }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip
            title={tooltip}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 360,
                  fontSize: 12,
                  lineHeight: "18px",
                  p: 1.5,
                  backgroundColor: "#1F2937",
                  "& .MuiTooltip-arrow": { color: "#1F2937" },
                },
              },
            }}
          >
            <HelpOutlineIcon sx={{
              fontSize: 13,
              color: colors.primary[300],
              cursor: "help",
              flexShrink: 0,
            }} />
          </Tooltip>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.875} sx={{ flexShrink: 0 }}>
        {na ? (
          <Typography sx={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: "20px",
            color: colors.neutral[500],
            fontVariantNumeric: "tabular-nums",
          }}>
            —
          </Typography>
        ) : (
          <>
            <Typography sx={{
              fontSize: 13,
              fontWeight: 600,
              lineHeight: "20px",
              color: warn ? WARN_INK : GREEN_DARK,
              fontVariantNumeric: "tabular-nums",
            }}>
              {value}
            </Typography>
            <PostureStatusIcon warn={warn} />
          </>
        )}
      </Stack>
    </Box>
  );
}

const pct = (n, d = 1) => (typeof n === "number" && isFinite(n) ? `${n.toFixed(d)}%` : "—");

export default function SSLTlsPostureCard({ overview, loading }) {
  const agg = overview?.aggregates;
  const t = overview?.traffic_24h;
  const total = agg?.total_sites ?? 0;
  const hasTlsTelemetry = Array.isArray(t?.tls_versions);
  const tls13 = hasTlsTelemetry
    ? t.tls_versions.find((v) => v.tls_version === "TLSv1.3")
    : null;
  const tls12 = hasTlsTelemetry
    ? t.tls_versions.find((v) => v.tls_version === "TLSv1.2")
    : null;
  const tls13Pct = hasTlsTelemetry ? (tls13?.pct ?? 0) : null;
  const tls12Pct = hasTlsTelemetry ? (tls12?.pct ?? 0) : null;
  const legacyCount = hasTlsTelemetry
    ? t.tls_versions
      .filter((v) => v.tls_version !== "TLSv1.3" && v.tls_version !== "TLSv1.2")
      .reduce((acc, v) => acc + (v.count || 0), 0)
    : null;

  const mixedOff = agg?.http_rewrite_off_count ?? 0;

  const rows = [
    {
      icon: MediationOutlinedIcon,
      label: "TLS 1.3 negotiated",
      value: hasTlsTelemetry ? pct(tls13Pct) : null,
      warn: hasTlsTelemetry ? tls13Pct <= 50 : false,
      ok: hasTlsTelemetry && tls13Pct > 50,
      na: !hasTlsTelemetry,
    },
    {
      icon: LayersOutlinedIcon,
      label: "TLS 1.2 negotiated",
      value: hasTlsTelemetry ? pct(tls12Pct) : null,
      warn: false,
      ok: hasTlsTelemetry,
      na: !hasTlsTelemetry,
    },
    {
      icon: BlockOutlinedIcon,
      label: "Legacy protocols (1.0/1.1)",
      value: legacyCount != null
        ? (legacyCount > 0 ? `${legacyCount} requests` : "disabled")
        : null,
      warn: legacyCount > 0,
      ok: legacyCount === 0,
      na: legacyCount == null,
    },
    {
      icon: GppBadOutlinedIcon,
      label: "HSTS",
      value: agg ? `${agg.hsts_enabled_count} / ${total} sites` : null,
      warn: agg ? agg.hsts_enabled_count < total : false,
      ok: agg && agg.hsts_enabled_count === total,
      na: !agg,
    },
    {
      icon: LinkOutlinedIcon,
      label: "OCSP stapling",
      value: null,
      warn: false,
      ok: false,
      na: true,
    },
    {
      icon: VpnKeyOutlinedIcon,
      label: "Forward secrecy",
      value: agg ? `${total} / ${total} sites` : null,
      warn: false,
      ok: !!agg,
      na: !agg,
      tooltip: "Forward secrecy is enforced by Layerd\u2019s TLS catalog — every selectable cipher is ECDHE / DHE / TLS 1.3 and static RSA / DH key exchange is forbidden. PFS is not a separate toggle; it\u2019s a property of every approved configuration.",
    },
    {
      icon: WarningAmberOutlinedIcon,
      label: "Mixed-content rewrite",
      value: agg
        ? (mixedOff === 0 ? "on" : `${mixedOff} site${mixedOff === 1 ? "" : "s"} off`)
        : null,
      warn: mixedOff > 0,
      ok: mixedOff === 0,
      na: !agg,
    },
  ];

  const scored = rows.filter((r) => !r.na);
  const healthy = scored.length > 0 && scored.every((r) => r.ok && !r.warn);

  return (
    <Box
      className="ssl-posture-checklist"
      sx={{
        alignSelf: "flex-start",
        width: "100%",
        height: "fit-content",
        ...certsCardShellSx,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={certsPanelHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <DoneAllOutlinedIcon sx={POSTURE_ROW_ICON_SX} />
          <Typography sx={certsCardTitleSx}>
            TLS posture
          </Typography>
          <Tooltip
            title="Separate from Aggregate TLS grade. Grade is certificate scan quality (A–F). Posture checks live config and traffic — HSTS, TLS versions, mixed-content rewrite. A high grade can still need attention here."
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 360,
                  fontSize: 12,
                  lineHeight: "18px",
                  color: "#fff",
                  backgroundColor: "#1F2937",
                  "& .MuiTooltip-arrow": { color: "#1F2937" },
                  p: 1.5,
                },
              },
            }}
          >
            <HelpOutlineIcon sx={{
              fontSize: 14,
              color: colors.primary[300],
              cursor: "help",
              flexShrink: 0,
            }} />
          </Tooltip>
        </Stack>
        {!loading && scored.length > 0 && (
          <Tooltip
            title={
              healthy
                ? "All posture checks passed."
                : `Attention on: ${scored.filter((r) => r.warn).map((r) => r.label).join(", ") || "one or more checks"}. Yellow rows below show what to fix — this does not change Aggregate TLS grade.`
            }
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 360,
                  fontSize: 12,
                  lineHeight: "18px",
                  color: "#fff",
                  backgroundColor: "#1F2937",
                  "& .MuiTooltip-arrow": { color: "#1F2937" },
                  p: 1.5,
                },
              },
            }}
          >
            <Box component="span" sx={{ display: "inline-flex", cursor: "help" }}>
              <PosturePill tone={healthy ? "ok" : "warn"}>
                {healthy ? "healthy" : "needs attention"}
              </PosturePill>
            </Box>
          </Tooltip>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <CircularProgress size={16} sx={{ color: colors.primary[300] }} />
        </Box>
      ) : scored.length === 0 ? (
        <Box sx={{ py: 2.5, px: "18px" }}>
          <Typography sx={{ fontSize: 13, color: colors.neutral[600] }}>
            No posture metrics available yet.
          </Typography>
        </Box>
      ) : (
        <Box sx={certsPostureCheckListSx}>
          {scored.map((r, i) => (
            <PostureCheckRow
              key={r.label}
              icon={r.icon}
              label={r.label}
              tooltip={r.tooltip}
              value={r.value}
              warn={r.warn}
              na={false}
              isLast={i === scored.length - 1}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
